import React, { useState, useEffect, useCallback } from 'react';
import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';

const ApiAnalyticsDashboard = () => {
  const getTodayString = () => new Date().toISOString().split('T')[0];

  // State Management
  const [endpoint, setEndpoint] = useState('/partner-v2');
  const [startDate, setStartDate] = useState(getTodayString());
  const [endDate, setEndDate] = useState(getTodayString());
  
  const [summary, setSummary] = useState({ total_calls: 0, success_calls: 0, failed_calls: 0 });
  const [topCallers, setTopCallers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const startDateTime = `${startDate} 00:00:00`;
      const endDateTime = `${endDate} 23:59:59`;

      const response = await axiosInstance.get('/analytics/dashboard', {
        params: {
          endpoint: endpoint,
          startDate: startDateTime,
          endDate: endDateTime,
          limit: 10
        }
      });

      if (response.data.success) {
        setSummary(response.data.data.summary);
        setTopCallers(response.data.data.top_callers);
      } else {
        setError('Failed to load analytics data.');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('An error occurred while fetching data.');
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, startDate, endDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return (
    <Wrapper>
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">API Analytics</h2>
        </div>

        {/* --- Filters Section --- */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-bold">Target Route</label>
                <select 
                  className="form-select" 
                  value={endpoint} 
                  onChange={(e) => setEndpoint(e.target.value)}
                >
                  <option value="/partner-v2">Partner V2 (/partner-v2)</option>
                  <option value="/device">IoT Devices (/device)</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold">Start Date</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={startDate}
                  max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-bold">End Date</label>
                <input 
                  type="date" 
                  className="form-control" 
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {isLoading && !error ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* --- Summary Cards Section --- */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card shadow-sm bg-light">
                  <div className="card-body text-center">
                    <h5 className="text-muted mb-2">Total Calls</h5>
                    <h2 className="mb-0 text-primary">{summary.total_calls.toLocaleString()}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm bg-light border-success border-start border-4">
                  <div className="card-body text-center">
                    <h5 className="text-muted mb-2">Success (2xx - 3xx)</h5>
                    <h2 className="mb-0 text-success">{summary.success_calls.toLocaleString()}</h2>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm bg-light border-danger border-start border-4">
                  <div className="card-body text-center">
                    <h5 className="text-muted mb-2">Failed (4xx - 5xx)</h5>
                    <h2 className="mb-0 text-danger">{summary.failed_calls.toLocaleString()}</h2>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Top Callers Table Section --- */}
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="mb-0 fw-bold">Top Callers</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4">Rank</th>
                        <th>Caller ID</th>
                        <th className="text-end pe-4">Total Calls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCallers.length > 0 ? (
                        topCallers.map((caller, index) => (
                          <tr key={`${caller.caller_id}-${index}`}>
                            <td className="ps-4 text-muted">#{index + 1}</td>
                            <td className="fw-medium">
                              {caller.entity_name ? (
                                <div>
                                  <span className="d-block text-dark">{caller.entity_name}</span>
                                  <span className="small text-muted">{caller.caller_id}</span>
                                </div>
                              ) : (
                                caller.caller_id || 'Unknown'
                              )}
                            </td>
                            <td className="text-end pe-4 fw-bold">
                              {caller.total_calls.toLocaleString()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center py-4 text-muted">
                            No caller data found for this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default ApiAnalyticsDashboard;